import { useState } from 'react';
import { X, Zap, Truck, AlertTriangle, FileEdit, Banknote, Upload, type LucideIcon } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import ContractPickerModal from '@/components/shared/contract-picker-modal';
import { useTutorial } from '@/components/tutorial/use-tutorial';
import { useContractsData, useSelectedContract } from '@/hooks/api/use-contracts';
import { pushEvent } from '@/lib/analytics';
import { hasOnlyContractApporteur43397, isWakamContract } from '@/lib/contracts';
import type { Contract } from '@/types/api/contract';

type ContractAction = 'document' | 'demenage';

interface MenuItem {
    icon: LucideIcon;
    label: string;
    action: string | (() => void);
    color: string;
    bg: string;
    opensDialog?: boolean;
}

const Infobulle = () => {
    const contracts = useContractsData();
    const contract = useSelectedContract();
    const isWakam = isWakamContract(contract);
    const hasOnlyApporteur43397 = hasOnlyContractApporteur43397(contracts);
    const navigate = useNavigate();
    const { isActive: isTutorialActive, currentStepData } = useTutorial();
    const isTutorialTarget = isTutorialActive && currentStepData?.id === 'infobulle-fab';
    const [active, setActive] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerAction, setPickerAction] = useState<ContractAction | null>(null);
    const [pickerContracts, setPickerContracts] = useState<Contract[]>([]);

    const canModifyRib = contracts.some(contract => contract.situation?.statutCode === '1');
    const demenageContracts = contracts.filter(c => !isWakam && c.situation?.statutCode === "1" && c.vehicule?.codeProduit !== '7311_QMCO' && c.vehicule?.codeProduit !== '7311_2MCO');

    const handleClick = (action: MenuItem['action'], itemName: string) => {
        pushEvent('quickLinksClick', {
            'quickLinksClick': {
                'item': itemName
            }
        });

        if (typeof action === 'function') {
            action();
        } else {
            navigate(action);
        }
        setActive(false);
    };

    const chooseContract = (action: ContractAction) => {
        let filteredContracts = contracts;

        if (action === 'document') {
            const withPieces = contracts.filter(c => (c.nb_pieces ?? 0) > 0);
            if (withPieces.length > 0) filteredContracts = withPieces;
        } else if (action === 'demenage') {
            filteredContracts = demenageContracts;
        }

        if (filteredContracts.length === 1) {
            handleContractAction(filteredContracts[0], action);
            return;
        }

        setPickerAction(action);
        setPickerContracts(filteredContracts);
        setPickerOpen(true);
    };

    const handleContractAction = (selectedContract: Contract, action: ContractAction | null) => {
        setActive(false);
        switch (action) {
            case 'document':
                return window.open(selectedContract.upload_app_link, '_blank');
            case 'demenage':
                return navigate(`/mes-contrats/mon-contrat/${selectedContract.id}/je-demenage`);
        }
    };

    const menuItems: MenuItem[] = [
        ...(hasOnlyApporteur43397 && demenageContracts.length > 0 ? [{
            icon: Truck,
            label: 'Je déménage',
            action: demenageContracts.length === 1
                ? `/mes-contrats/mon-contrat/${demenageContracts[0].id}/je-demenage`
                : () => chooseContract('demenage'),
            color: 'text-primary',
            bg: 'bg-primary/10',
            opensDialog: demenageContracts.length > 1,
        }] : []),
        ...(!isWakam ? [{
            icon: AlertTriangle,
            label: 'Je déclare un sinistre',
            action: '/mes-sinistres',
            color: 'text-destructive',
            bg: 'bg-destructive/10',
        }] : []),
        ...(isWakam ? [
            {
                icon: FileEdit,
                label: 'Je modifie mon contrat',
                action: () => window.open(contract?.avenant_link, '_blank'),
                color: 'text-accent',
                bg: 'bg-accent/10',
            },
        ] : []),
        ...(canModifyRib ? [{
            icon: Banknote,
            label: 'Je change de RIB',
            action: '/mon-profil/je-change-mes-informations-bancaires',
            color: 'text-success',
            bg: 'bg-success/10',
        }] : []),
        {
            icon: Upload,
            label: "J'envoie des documents",
            action: () => chooseContract('document'),
            color: 'text-primary',
            bg: 'bg-primary/10',
            opensDialog: contracts.length > 1,
        },
    ];

    return (
        <>
            {/* Backdrop overlay */}
            <div
                className={`fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${active && !isTutorialActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setActive(false)}
                role="presentation"
            />

            {/* Menu items */}
            <div
                id="infobulle-menu"
                role="menu"
                aria-label="Accès rapide"
                aria-hidden={!active || isTutorialActive}
                /* Valeur d'origine conservée : React 19 traite `inert=""` comme faux (attribut non posé). */
                {...(!active || isTutorialActive ? { inert: '' as unknown as boolean } : {})}
                className={`fixed bottom-20 right-4 sm:bottom-28 sm:right-6 md:bottom-28 md:right-12 z-50 flex flex-col items-end gap-1.5 sm:gap-2 ${active && !isTutorialActive ? '' : 'pointer-events-none'}`}
            >
                {menuItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={index}
                            type="button"
                            role="menuitem"
                            tabIndex={active && !isTutorialActive ? 0 : -1}
                            {...(item.opensDialog ? { 'aria-haspopup': 'dialog' } : {})}
                            className="group flex items-center gap-2 sm:gap-3 px-4 py-2 rounded-xl bg-card border shadow-sm cursor-pointer transition-all duration-200 ease-out hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 hover:scale-[1.03] active:scale-100 origin-right"
                            style={{
                                opacity: active ? 1 : 0,
                                transform: active ? 'translateX(0)' : 'translateX(20px)',
                                transition: `opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s`,
                                pointerEvents: active ? 'auto' : 'none',
                            }}
                            onClick={() => handleClick(item.action, item.label)}
                        >
                            <span className="text-xs sm:text-sm font-medium text-foreground whitespace-nowrap">
                                {item.label}
                            </span>
                            <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${item.bg} transition-transform duration-200 group-hover:scale-110 shrink-0`}>
                                <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${item.color}`} aria-hidden="true" focusable="false" />
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* FAB button */}
            <button
                data-tutorial="infobulle-fab"
                className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-12 md:right-12 z-50 flex items-center gap-2 cursor-pointer rounded-full shadow-lg transition-all duration-300 ease-out hover:shadow-xl hover:scale-105 active:scale-95 ${isTutorialActive && !isTutorialTarget ? 'opacity-0 pointer-events-none' : ''} ${
                    active
                        ? 'bg-card border border-border w-10 h-10 sm:w-12 sm:h-12 justify-center'
                        : 'bg-warning text-warning-foreground px-4 h-10 sm:h-12'
                }`}
                onClick={() => setActive(!active)}
                aria-expanded={active}
                aria-haspopup="menu"
                aria-controls="infobulle-menu"
                aria-label="Accès rapide"
            >
                {active ? (
                    <X className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" aria-hidden="true" />
                ) : (
                    <>
                        <Zap className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                        <span className="hidden sm:inline text-sm font-medium">Accès rapide</span>
                    </>
                )}
            </button>

            {/* Contract picker modal */}
            <ContractPickerModal
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={(c) => handleContractAction(c, pickerAction)}
                contracts={pickerContracts}
            />
        </>
    );
};

export default Infobulle;
